import * as Yup from "yup";
import { GroupBy, getDateKey } from "../reportCommonHelpers";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { WorkingHoursData } from "app/data/types";
import moment from "moment";
import { sortBy } from "lodash";
import { calculateDeltaSum } from "app/data/constants";

export const reportsWorkingHoursFormInitialValues = {
  objectType: [],
  group: [],
  object: [],
  key: [],
  daysOfWeek: ["0", "1", "2", "3", "4", "5", "6"],
  startTime: "00:00",
  endTime: "23:59",
  assignedToAllObjects: true,
  showInactiveObjects: false
};

export const reportsWorkingHoursFormValidationSchema: Yup.Schema = Yup.object({
  objectType: Yup.array().of(Yup.string()).label("Object Types"),
  group: Yup.array().of(Yup.string()).label("Groups"),
  assignedToAllObjects: Yup.boolean(),
  object: Yup.array()
    .of(Yup.string())
    .label("Objects")
    .when("assignedToAllObjects", {
      is: false,
      then: (schema) =>
        schema.min(1, "At least one object must be selected").required(),
      otherwise: (schema) => schema
    }),
  key: Yup.array().of(Yup.string()).label("Key")
});

type WorkingHoursWip = {
  groupedInfo: WorkingHoursData["groupedInfo"];
  detailsByDate: Map<string, { date: string; data: any[] }>;
};

const earliestSessionTime = (detail: { data: any[] }): number | null => {
  let min: number | null = null;
  for (const session of detail.data) {
    const time = new Date(session.start).getTime();
    if (!Number.isNaN(time) && (min === null || time < min)) {
      min = time;
    }
  }
  return min;
};

/** Sort buckets chronologically using session starts (date keys are display labels from getDateKey). */
const compareDetailsByEarliestSession = (
  a: { date: string; data: any[] },
  b: { date: string; data: any[] }
): number => {
  const aTime = earliestSessionTime(a);
  const bTime = earliestSessionTime(b);

  if (aTime !== null && bTime !== null) {
    if (aTime !== bTime) {
      return aTime - bTime;
    }
    return a.date.localeCompare(b.date);
  }
  if (aTime !== null) {
    return -1;
  }
  if (bTime !== null) {
    return 1;
  }
  return a.date.localeCompare(b.date);
};

export const formattedWorkingHours = (
  data: any,
  groupBy: GroupBy
): WorkingHoursData[] => {
  if (!data?.length) {
    return [];
  }

  const bySerial = new Map<string, WorkingHoursWip>();

  data.forEach((item: any) => {
    item?.groupedInfo?.forEach((groupedItem: any) => {
      const serialNumber = groupedItem.serialNumber;
      let entry = bySerial.get(serialNumber);

      if (!entry) {
        entry = {
          groupedInfo: {
            name: groupedItem.name,
            uniqueId: groupedItem.uniqueId,
            contactHours: groupedItem.contactHours,
            operatingHours: groupedItem.operatingHours,
            ptoHours: groupedItem.ptoHours,
            serialNumber: groupedItem.serialNumber
          },
          detailsByDate: new Map()
        };
        bySerial.set(serialNumber, entry);
      } else {
        entry.groupedInfo.contactHours += groupedItem.contactHours;
        entry.groupedInfo.operatingHours += groupedItem.operatingHours;
        entry.groupedInfo.ptoHours += groupedItem.ptoHours;
      }

      item.detailedInfo?.forEach((detail: any) => {
        if (detail.serialNumber === serialNumber && detail.end !== null) {
          const date = getDateKey(detail.start, groupBy);
          let day = entry!.detailsByDate.get(date);
          if (!day) {
            day = { date, data: [] };
            entry!.detailsByDate.set(date, day);
          }

          const existingDetailIndex = day.data.findIndex(
            (existingDetail) => existingDetail.sessionId === detail.sessionId
          );

          if (existingDetailIndex !== -1) {
            day.data[existingDetailIndex] = { ...detail };
          } else {
            day.data.push({ ...detail });
          }
        }
      });
    });
  });

  const formattedData: WorkingHoursData[] = Array.from(bySerial.values()).map(
    ({ groupedInfo, detailsByDate }) => ({
      groupedInfo,
      details: Array.from(detailsByDate.values()).sort(
        compareDetailsByEarliestSession
      )
    })
  );

  return sortBy(formattedData, ["groupedInfo.name"]);
};

const workingHoursPDFextractedRows = (items: any) => {
  return items.map((item: any) => {
    return [
      item?.name,
      item?.sessionId,
      moment(item?.start).format("DD/MM/YY HH:mm:ss") || "N/A",
      moment(item?.end).format("DD/MM/YY HH:mm:ss") || "N/A",
      item?.duration,
      item?.startAddress,
      item?.endAddress,
      item?.startValue?.toFixed(1),
      item?.endValue?.toFixed(1) || "N/A",
      item?.calculatedDelta?.toFixed(1) || "N/A",
      getSessionTypeLabel(item?.sessionType)
    ];
  });
};

export const generateFormattedWorkingHoursPdf = (
  doc: jsPDF,
  report: any[],
  columns: string[]
) => {
  let printSummary = true;
  report.forEach((groupedInfo: any, index: number) => {
    const { name, serialNumber, contactHours, operatingHours, ptoHours } =
      groupedInfo.groupedInfo;
    const details = groupedInfo.details;
    const startDate = details[0].date;
    const endDate = details[details.length - 1].date;

    if (index > 0) {
      doc.addPage();
    }
    doc.setFontSize(10);

    //print summary on the start of each object
    if (printSummary) {
      doc.setFontSize(11);
      doc.text(`Name: ${name}`, 65, index === 0 ? 25 : 15);
      doc.text(`Serial Number: ${serialNumber}`, 65, index === 0 ? 30 : 20);
      doc.text(`Start Date: ${startDate}`, 65, index === 0 ? 35 : 25);
      doc.text(`End Date: ${endDate}`, 65, index === 0 ? 40 : 30);
      doc.text(
        `Total Ignition Hours: ${contactHours?.toFixed(1)}`,
        65,
        index === 0 ? 45 : 35
      );
      doc.text(
        `Total Running Hours: ${operatingHours?.toFixed(1)}`,
        65,
        index === 0 ? 50 : 40
      );
      doc.text(
        `Total PTO Hours: ${ptoHours?.toFixed(1)}`,
        65,
        index === 0 ? 55 : 45
      );
      doc.setFontSize(10);
    }

    let previousDate: any = null;

    details.forEach(({ date, data }: any, i: number) => {
      if (date !== previousDate && previousDate !== null) {
        doc.addPage();
      }

      previousDate = date;

      doc.text(`Date: ${date}`, 10, index === 0 ? 25 : 15);
      doc.text(
        `Week number: ${moment(date).week()}`,
        10,
        index === 0 ? 30 : 20
      );
      doc.text(`Name: ${name}`, 10, index === 0 ? 35 : 25);
      doc.text(calculateDeltaSum(data, 2) || "", 10, index === 0 ? 40 : 30);
      doc.text(calculateDeltaSum(data, 1) || "", 10, index === 0 ? 45 : 35);
      doc.text(calculateDeltaSum(data, 3) || "", 10, index === 0 ? 50 : 40);

      const compiledData = data.map((item: any) => ({
        ...item,
        date
      }));

      autoTable(doc, {
        head: [columns],
        body: workingHoursPDFextractedRows(compiledData),
        startY: printSummary && index === 0 ? 65 : 60,
        styles: { overflow: "linebreak" },
        columnStyles: {
          2: { cellWidth: 25 },
          3: { cellWidth: 25 }
        }
      });
      printSummary = false;
    });
    printSummary = true;
  });
};

export const getSessionTypeLabel = (sessionType: number) => {
  switch (sessionType) {
    case 1:
      return "Running Hours";
    case 2:
      return "Ignition Hours";
    case 3:
      return "PTO Hours";
    default:
      return "-";
  }
};
